/**
 * Publish Button Component
 * 
 * Button to publish the current draft to production.
 * Shows loading state during publish and disabled when not in draft mode.
 */

import { Button, Tooltip, Spinner, makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';
import { ArrowUpload20Regular } from '@fluentui/react-icons';
import { useDraftModeOptional } from '@/contexts/DraftModeContext';
import {
  useDraftModeStore,
  selectIsDraftMode,
  useAreProdAndDraftEqual,
} from '@/stores/draftModeStore';
import { usePublishDraft } from '@/hooks/usePublishDraft';
import { useGuardedModeSwitch } from '@/hooks/useGuardedModeSwitch';
import { useNotificationStore } from '@/stores/notificationStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { isWorkflowEmpty } from '@/utils/validation';

const useStyles = makeStyles({
  button: {
    minWidth: 'auto',
  },
  publishButton: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
    },
  },
  // Center variant - icon above label (vertical layout)
  centerButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    minWidth: '56px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  centerButtonIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1,
  },
  centerButtonLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    lineHeight: 1,
  },
});

export interface PublishButtonProps {
  /** Workflow name to publish */
  workflowName: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Callback after successful publish */
  onPublished?: () => void;
  /** Button variant - 'default' for horizontal, 'center' for vertical icon+label */
  variant?: 'default' | 'center';
}

export const PublishButton: React.FC<PublishButtonProps> = ({
  workflowName,
  disabled = false,
  onPublished,
  variant = 'default',
}) => {
  const styles = useStyles();
  const draftMode = useDraftModeOptional();
  // Fallback to the store when this button is rendered above DraftModeProvider
  // (e.g., in the workflow sub-header). The store stays in sync with context.
  const storeIsDraftMode = useDraftModeStore(selectIsDraftMode);
  const storeAreEqual = useAreProdAndDraftEqual();
  const addNotification = useNotificationStore((state) => state.addNotification);
  const modeSwitch = useGuardedModeSwitch({ workflowName });

  const isDraftMode = draftMode?.isDraftMode ?? storeIsDraftMode;
  const areProdAndDraftEqual = draftMode?.areProdAndDraftEqual ?? storeAreEqual;

  // Detect an empty workflow (no triggers AND no actions). We block publish
  // in this state — the runtime would accept it but the resulting prod
  // workflow has nothing to run.
  const isEmptyWorkflow = useWorkflowStore((state) => isWorkflowEmpty(state.workflow));
  const workflowCodeBlocked = useWorkflowStore(
    (state) => state.codeBufferDirty || state.codeBufferParseError !== null,
  );
  const nodeCodeBlocked = useNodeEditorStore(
    (state) => state.nodeCodeDraft.isDirty,
  );
  const codeEditingBlocked = workflowCodeBlocked || nodeCodeBlocked;
  
  const { publish, isPublishing } = usePublishDraft({
    workflowName,
    onSuccess: ({ wasRepublish }) => {
      addNotification({
        type: 'success',
        title: wasRepublish ? 'Republished successfully' : 'Published successfully',
        message: wasRepublish
          ? 'Workflow republished. No changes since last publish.'
          : 'Your workflow is now live.',
        actionButton: {
          label: 'Open published workflow',
          onClick: modeSwitch.requestSwitchToPublished,
        },
      });
      onPublished?.();
    },
    onError: (err) => {
      addNotification({
        type: 'error',
        title: 'Publish failed',
        message: err.message,
      });
    },
  });

  // Don't show if not in draft mode
  if (!isDraftMode) {
    return null;
  }

  // Publish is always enabled in draft mode (force-republish supports
  // recovery from failed deploys, runtime config changes, webhook URL
  // rotation, etc.) — *except* when the workflow is empty. An empty
  // workflow has nothing to run and shouldn't be promoted to prod.
  const isDisabled =
    disabled || isPublishing || isEmptyWorkflow || codeEditingBlocked;

  const tooltipContent = isPublishing
    ? 'Publishing...'
    : codeEditingBlocked
    ? 'Apply or revert code changes before publishing.'
    : isEmptyWorkflow
    ? 'Add a trigger to get started before publishing.'
    : areProdAndDraftEqual
    ? 'Workflow is up to date. Click to republish.'
    : 'Publish draft to production';

  const handlePublish = async () => {
    await publish();
  };

  // Center variant - icon above label (vertical layout)
  if (variant === 'center') {
    return (
      <>
        <Tooltip content={tooltipContent} relationship="label">
          <button
            type="button"
            className={styles.centerButton}
            onClick={handlePublish}
            disabled={isDisabled}
            aria-label="Publish"
          >
            <span className={styles.centerButtonIcon}>
              {isPublishing ? <Spinner size="tiny" /> : <ArrowUpload20Regular />}
            </span>
            <span className={styles.centerButtonLabel}>Publish</span>
          </button>
        </Tooltip>
        {modeSwitch.dialog}
      </>
    );
  }

  // Default variant - horizontal layout
  return (
    <>
      <Tooltip content={tooltipContent} relationship="label">
        <Button
          className={mergeClasses(styles.button, !isDisabled && styles.publishButton)}
          appearance={isDisabled ? 'subtle' : 'primary'}
          icon={isPublishing ? <Spinner size="tiny" /> : <ArrowUpload20Regular />}
          onClick={handlePublish}
          disabled={isDisabled}
          aria-label={isPublishing ? 'Publishing' : 'Publish'}
        >
          {isPublishing ? 'Publishing…' : 'Publish'}
        </Button>
      </Tooltip>
      {modeSwitch.dialog}
    </>
  );
};

export default PublishButton;
